/**
 * Organization management API schemas
 */

import { z } from 'zod';
import { CredentialType, Role, Status } from '@pillar-portal/database/dist/frontend';
import {
    JsonObjectSchema,
    MessageResponseSchema,
    PositiveIntSchema,
} from './common.schemas';
import { NumericIpgSystemIdSchema, NumericIpwSystemIdSchema } from './third-party-system.schemas';
import {
    BusinessDetailsSchema,
    CustomerPortalPaymentRequestsConfigSchema,
    IpwConfigDealertrackSchema,
    PlatformPaymentMethodsSchema,
} from '../backend_config.types';
import { PermissionGrantAuditFindingCodeSchema } from '../permission.types';
import { MerchantTimeZoneSchema } from '../timezone.utils';
import type {
    ApplyOrganizationUsersCsvImportRequest,
    ApplyOrganizationUsersCsvImportResult,
    ApplyOrganizationUsersCsvImportResponse,
    CreateOrganizationDocumentTemplateRequest,
    DeleteOrganizationResponse,
    CreateIsoResponse,
    CreateMerchantResponse,
    DocumentTemplateKind,
    DocumentTemplateOutputMime,
    DocumentTemplatePayloadType,
    DocumentTemplateSelector,
    DocumentTemplateValidationIssue,
    DocumentTemplateValidationIssueCode,
    GetOrganizationResponse,
    GetMerchantChecklistCheckResponse,
    GetMerchantChecklistResponse,
    ListOrganizationDocumentTemplatesResponse,
    MerchantBackupCredentialRecord,
    MerchantChecklistCredentialIssue,
    MerchantChecklistPermissionIssue,
    MerchantBackupLogicalTerminalRecord,
    MerchantBackupMerchantAccountRecord,
    MerchantChecklistTerminalIssue,
    MerchantChecklistUserIssue,
    MerchantBackupOrganizationRecord,
    MerchantBackupPermissionsBundleRecord,
    MerchantBackupPermissionsPolicyGrantRecord,
    MerchantBackupRoleAssignmentRecord,
    MerchantBackupTerminalDeviceRecord,
    MerchantBackupUserRecord,
    MerchantBackupV1,
    OrganizationUserCsvImportMatchedUserSummary,
    OrganizationUserCsvImportPreviewRow,
    OrganizationUserCsvImportRowClassification,
    OrganizationUserCsvImportRole,
    OrganizationUserCsvImportUserData,
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    OrganizationDocumentTemplateOverrideDescriptor,
    OrganizationDocumentTemplateRegistryEntry,
    OrganizationSearchMerchantAccountSummaryItem,
    PreviewOrganizationUsersCsvImportRequest,
    PreviewOrganizationUsersCsvImportResponse,
    SetOrganizationDocumentTemplateActiveRequest,
    SetOrganizationParentResponse,
    UpdateOrganizationDocumentTemplateRequest,
    UpdateOrganizationResponse,
    ValidateOrganizationDocumentTemplateFailureResponse,
    ValidateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateResponse,
    ValidateOrganizationDocumentTemplateSuccessResponse,
} from '../organization.types';

export const PaymentRequestEmailCcSchema: z.ZodType<string[]> = z.array(
    z.string().email()
);

/**
 * Organization list item
 */
export const OrganizationSearchMerchantAccountSummaryItemSchema: z.ZodType<OrganizationSearchMerchantAccountSummaryItem> =
    z.object({
        processor_mid: z.string(),
        surcharge_enabled: z.boolean(),
        surcharge_pct: z.number(),
    });

export const OrganizationListItemSchema: z.ZodType<OrganizationListItem> = z.object({
    organization_id: z.number(),
    organization_uuid: z.string().uuid().optional(),
    name: z.string(),
    omnisearch_text: z.string().optional(),
    processor_mids: z.array(z.string()).optional(),
    merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_id: z.number().nullable(),
    parent_organization_uuid: z.string().uuid().nullable().optional(),
    status: z.enum(Status),
    businessDetails: BusinessDetailsSchema.optional(),
    detected_time_zone: MerchantTimeZoneSchema.optional(),
    dealertrackReconciliation: IpwConfigDealertrackSchema.shape.reconciliation.optional(),
    merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
    customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
    merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
    merchant_count: z.number().optional(),
});

export const OrganizationOmnisearchListItemSchema: z.ZodType<OrganizationOmnisearchListItem> =
    z.object({
        organization_uuid: z.string().uuid(),
        name: z.string(),
        omnisearch_text: z.string().optional(),
        processor_mids: z.array(z.string()).optional(),
        merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
        is_iso: z.boolean(),
        is_merchant: z.boolean(),
        is_customer: z.boolean(),
        parent_organization_uuid: z.string().uuid().nullable(),
        status: z.enum(Status),
        businessDetails: BusinessDetailsSchema.optional(),
        detected_time_zone: MerchantTimeZoneSchema.optional(),
        dealertrackReconciliation: IpwConfigDealertrackSchema.shape.reconciliation.optional(),
        merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
        customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
        merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
        enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
        enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
        merchant_count: z.number().optional(),
    });

/**
 * Create ISO request
 */
export const CreateIsoRequestSchema = z.object({
    name: z.string().min(1),
    parent_organization_uuid: z.string().uuid().optional(),
});

/**
 * Create merchant request
 */
export const CreateMerchantRequestSchema = z.object({
    name: z.string().min(1),
    parent_organization_uuid: z.string().uuid().optional(),
});

/**
 * Assign merchant to ISO request
 */
export const AssignMerchantToIsoRequestSchema = z.object({
    merchant_organization_uuid: z.string().uuid(),
    iso_organization_uuid: z.string().uuid().nullable(),
});

/**
 * Update organization request
 */
export const UpdateOrganizationRequestSchema = z.object({
    name: z.string().min(1).optional(),
    status: z.enum(Status).optional(),
    businessDetails: BusinessDetailsSchema.optional(),
    dealertrackReconciliation: IpwConfigDealertrackSchema.shape.reconciliation.optional(),
    merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
    customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
    merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
});

/**
 * Assign merchant to ISO response
 */
export const AssignMerchantToIsoResponseSchema = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    parent_organization_uuid: z.string().uuid().nullable(),
});

/**
 * Set organization parent request
 */
export const SetOrganizationParentRequestSchema = z.object({
    organization_uuid: z.string().uuid(),
    parent_organization_uuid: z.string().uuid().nullable(),
});

/**
 * Set organization parent response
 */
export const SetOrganizationParentResponseSchema: z.ZodType<SetOrganizationParentResponse> =
    z.lazy(() => UpdateOrganizationResponseSchema);

/**
 * Delete organization response
 */
export const DeleteOrganizationResponseSchema: z.ZodType<DeleteOrganizationResponse> =
    MessageResponseSchema;

/**
 * Create ISO response
 */
export const CreateIsoResponseSchema: z.ZodType<CreateIsoResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    status: z.enum(Status),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema),
});

/**
 * Create merchant response
 */
export const CreateMerchantResponseSchema: z.ZodType<CreateMerchantResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    status: z.enum(Status),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema),
});

/**
 * Update organization response
 */
export const UpdateOrganizationResponseSchema: z.ZodType<UpdateOrganizationResponse> = z.object({
    organization_uuid: z.string().uuid(),
    name: z.string(),
    omnisearch_text: z.string().optional(),
    processor_mids: z.array(z.string()).optional(),
    merchant_accounts: z.array(OrganizationSearchMerchantAccountSummaryItemSchema).optional(),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    parent_organization_uuid: z.string().uuid().nullable(),
    status: z.enum(Status),
    businessDetails: BusinessDetailsSchema.optional(),
    detected_time_zone: MerchantTimeZoneSchema.optional(),
    dealertrackReconciliation: IpwConfigDealertrackSchema.shape.reconciliation.optional(),
    merchantPaymentMethods: PlatformPaymentMethodsSchema.optional(),
    customerPortalPaymentRequests: CustomerPortalPaymentRequestsConfigSchema.optional(),
    merchantPaymentConfirmationEmailCc: PaymentRequestEmailCcSchema.optional(),
    enable_ipg_system_ids: z.array(NumericIpgSystemIdSchema).optional(),
    enable_ipw_system_ids: z.array(NumericIpwSystemIdSchema).optional(),
    merchant_count: z.number().optional(),
});

/**
 * Get organization response
 */
export const GetOrganizationResponseSchema: z.ZodType<GetOrganizationResponse> =
    UpdateOrganizationResponseSchema;

/**
 * Common param/query schemas
 */
export const OrganizationIdParamSchema: z.ZodType<number> = PositiveIntSchema;
export const OrganizationUuidParamSchema: z.ZodType<string> = z.string().uuid();
export const IsoIdQuerySchema: z.ZodType<number> = PositiveIntSchema;

const JsonRecordSchema = z.record(z.string(), z.unknown());

export const OrganizationUserCsvImportRowClassificationSchema: z.ZodType<OrganizationUserCsvImportRowClassification> =
    z.enum([
        'insert',
        'noop',
        'update',
        'csv_duplicate_error',
        'db_ambiguous_match_error',
    ]);

export const OrganizationUserCsvImportRoleSchema: z.ZodType<OrganizationUserCsvImportRole> =
    z.enum(['PRINCIPAL', 'ADMIN', 'REP']);

export const OrganizationUserCsvImportUserDataSchema: z.ZodType<OrganizationUserCsvImportUserData> =
    z.object({
        first_name: z.string().min(1).optional(),
        last_name: z.string().min(1).optional(),
        emails: z.array(z.string().email()),
        phones: z.array(z.string().min(1)),
        primary_email: z.string().email(),
        primary_phone: z.string().min(1).optional(),
        role: OrganizationUserCsvImportRoleSchema,
        policies: z.array(z.string().min(1)),
    });

export const OrganizationUserCsvImportMatchedUserSummarySchema: z.ZodType<OrganizationUserCsvImportMatchedUserSummary> =
    z.object({
        user_uuid: z.string().uuid(),
        first_name: z.string().min(1).optional(),
        last_name: z.string().min(1).optional(),
        emails: z.array(z.string().email()),
        phones: z.array(z.string().min(1)),
        primary_email: z.string().email(),
        primary_phone: z.string().min(1).optional(),
        role: OrganizationUserCsvImportRoleSchema,
        policies: z.array(z.string().min(1)),
    });

export const PreviewOrganizationUsersCsvImportRequestSchema: z.ZodType<PreviewOrganizationUsersCsvImportRequest> =
    z.object({
        csv_text: z.string().min(1),
    });

export const OrganizationUserCsvImportPreviewRowSchema: z.ZodType<OrganizationUserCsvImportPreviewRow> =
    z.object({
        row_index: z.number().int().positive(),
        classification: OrganizationUserCsvImportRowClassificationSchema,
        row_data: OrganizationUserCsvImportUserDataSchema,
        row_errors: z.array(z.string().min(1)).optional(),
        existing_user: OrganizationUserCsvImportMatchedUserSummarySchema.optional(),
        conflicting_users: z
            .array(OrganizationUserCsvImportMatchedUserSummarySchema)
            .optional(),
    });

export const PreviewOrganizationUsersCsvImportResponseSchema: z.ZodType<PreviewOrganizationUsersCsvImportResponse> =
    z.object({
        rows: z.array(OrganizationUserCsvImportPreviewRowSchema),
    });

export const ApplyOrganizationUsersCsvImportRequestSchema: z.ZodType<ApplyOrganizationUsersCsvImportRequest> =
    z.object({
        csv_text: z.string().min(1),
        selected_row_indexes: z.array(z.number().int().positive()).min(1),
    });

export const ApplyOrganizationUsersCsvImportResultClassificationSchema: z.ZodType<
    ApplyOrganizationUsersCsvImportResult['classification']
> = z.enum(['insert', 'update']);

export const ApplyOrganizationUsersCsvImportResultSchema: z.ZodType<ApplyOrganizationUsersCsvImportResult> =
    z.object({
        row_index: z.number().int().positive(),
        classification: ApplyOrganizationUsersCsvImportResultClassificationSchema,
        user_uuid: z.string().uuid(),
    });

export const ApplyOrganizationUsersCsvImportResponseSchema: z.ZodType<ApplyOrganizationUsersCsvImportResponse> =
    z.object({
        results: z.array(ApplyOrganizationUsersCsvImportResultSchema),
    });

export const DocumentTemplateKindSchema: z.ZodType<DocumentTemplateKind> = z.enum([
    'PAYMENT_RECEIPT',
    'INVOICE',
    'PAYMENT_REQUEST_EMAIL',
    'PAYMENT_REQUEST_SMS',
]);

export const DocumentTemplateOutputMimeSchema: z.ZodType<DocumentTemplateOutputMime> = z.enum([
    'TEXT',
    'HTML',
]);

export const DocumentTemplatePayloadTypeSchema: z.ZodType<DocumentTemplatePayloadType> = z.enum([
    'TRANSACTION_DETAILS',
    'PAYMENT_REQUEST',
    'PAYMENT_TRANSACTION',
]);

export const DocumentTemplateSelectorSchema: z.ZodType<DocumentTemplateSelector> = z.object({
    template_kind: DocumentTemplateKindSchema,
    template_output_mime: DocumentTemplateOutputMimeSchema,
    payload_type: DocumentTemplatePayloadTypeSchema,
    payload_discriminator: z.string().min(1),
    payload_type_version: z.number().int().nonnegative(),
    selector_json: JsonObjectSchema.optional(),
});

export const OrganizationDocumentTemplateRegistryEntrySchema: z.ZodType<OrganizationDocumentTemplateRegistryEntry> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        default_template_source: z.string().min(1),
        allowed_root_variables: z.array(z.string().min(1)),
    });

export const OrganizationDocumentTemplateOverrideDescriptorSchema: z.ZodType<OrganizationDocumentTemplateOverrideDescriptor> =
    z.object({
        document_template_uuid: z.string().uuid(),
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        content_sha256: z.string().min(1),
        is_active: z.boolean(),
    });

export const ListOrganizationDocumentTemplatesResponseSchema: z.ZodType<ListOrganizationDocumentTemplatesResponse> =
    z.object({
        registry_entries: z.array(OrganizationDocumentTemplateRegistryEntrySchema),
        overrides: z.array(OrganizationDocumentTemplateOverrideDescriptorSchema),
    });

export const DocumentTemplateValidationIssueCodeSchema: z.ZodType<DocumentTemplateValidationIssueCode> =
    z.enum([
        'UNSUPPORTED_SELECTOR',
        'TEMPLATE_TOO_LARGE',
        'PARSE_ERROR',
        'FORBIDDEN_TAG',
        'FORBIDDEN_FILTER',
        'FORBIDDEN_VARIABLE_ROOT',
    ]);

export const DocumentTemplateValidationIssueSchema: z.ZodType<DocumentTemplateValidationIssue> =
    z.object({
        code: DocumentTemplateValidationIssueCodeSchema,
        message: z.string().min(1),
    });

export const ValidateOrganizationDocumentTemplateRequestSchema: z.ZodType<ValidateOrganizationDocumentTemplateRequest> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        template_source: z.string().min(1),
    });

export const ValidateOrganizationDocumentTemplateSuccessResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateSuccessResponse> =
    z.object({
        valid: z.literal(true),
        referenced_root_variables: z.array(z.string().min(1)),
        used_filter_names: z.array(z.string().min(1)),
        used_tag_names: z.array(z.string().min(1)),
    });

export const ValidateOrganizationDocumentTemplateFailureResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateFailureResponse> =
    z.object({
        valid: z.literal(false),
        issues: z.array(DocumentTemplateValidationIssueSchema),
    });

export const ValidateOrganizationDocumentTemplateResponseSchema: z.ZodType<ValidateOrganizationDocumentTemplateResponse> =
    z.union([
        ValidateOrganizationDocumentTemplateSuccessResponseSchema,
        ValidateOrganizationDocumentTemplateFailureResponseSchema,
    ]);

export const CreateOrganizationDocumentTemplateRequestSchema: z.ZodType<CreateOrganizationDocumentTemplateRequest> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        is_active: z.boolean().optional(),
    });

export const UpdateOrganizationDocumentTemplateRequestSchema: z.ZodType<UpdateOrganizationDocumentTemplateRequest> =
    z.object({
        selector: DocumentTemplateSelectorSchema,
        name: z.string().min(1),
        description: z.string().min(1).optional(),
        template_source: z.string().min(1),
        is_active: z.boolean().optional(),
    });

export const SetOrganizationDocumentTemplateActiveRequestSchema: z.ZodType<SetOrganizationDocumentTemplateActiveRequest> =
    z.object({
        is_active: z.boolean(),
    });

export const MerchantBackupOrganizationRecordSchema: z.ZodType<MerchantBackupOrganizationRecord> = z.object({
    organization_uuid: z.string().uuid(),
    parent_organization_uuid: z.string().uuid().nullable(),
    name: z.string().min(1),
    is_iso: z.boolean(),
    is_merchant: z.boolean(),
    is_customer: z.boolean(),
    status: z.enum(Status),
    org_config: JsonRecordSchema,
    omnisearch_text: z.string(),
});

export const MerchantBackupUserRecordSchema: z.ZodType<MerchantBackupUserRecord> = z.object({
    user_uuid: z.string().uuid(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    emails: z.array(z.string().min(1)),
    primary_email: z.string().min(1),
    password_hash: z.string().min(1),
    totp_secret: z.string().nullable(),
    totp_enabled: z.boolean(),
    status: z.enum(Status),
    default_role_assignment_uuid: z.string().uuid().nullable(),
    omnisearch_text: z.string(),
});

export const MerchantBackupPermissionsBundleRecordSchema: z.ZodType<MerchantBackupPermissionsBundleRecord> =
    z.object({
        permissions_bundle_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        name: z.string().min(1),
        description: z.string().nullable(),
        permissions: z.array(z.unknown()),
        is_personal: z.boolean(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupRoleAssignmentRecordSchema: z.ZodType<MerchantBackupRoleAssignmentRecord> =
    z.object({
        role_assignment_uuid: z.string().uuid(),
        user_uuid: z.string().uuid(),
        organization_uuid: z.string().uuid().nullable(),
        role: z.enum(Role),
        user_config: JsonRecordSchema,
        permissions_bundle_uuid: z.string().uuid(),
    });

export const MerchantBackupPermissionsPolicyGrantRecordSchema: z.ZodType<MerchantBackupPermissionsPolicyGrantRecord> =
    z.object({
        permissions_policy_grant_uuid: z.string().uuid(),
        role_assignment_uuid: z.string().uuid(),
        permissions_bundle_uuid: z.string().uuid(),
    });

export const MerchantBackupCredentialRecordSchema: z.ZodType<MerchantBackupCredentialRecord> =
    z.object({
        credential_uuid: z.string().uuid(),
        credential_type: z.enum(CredentialType),
        organization_uuid: z.string().uuid(),
        system_id: z.number().int(),
        account_id: z.string().min(1),
        encrypted_credentials_base64: z.string().min(1),
        encryption_algorithm: z.string().min(1),
        credential_label: z.string().nullable(),
        masked_preview: z.string().nullable(),
        public_fields: JsonRecordSchema,
        applies_to_children: z.boolean(),
        is_active: z.boolean(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupMerchantAccountRecordSchema: z.ZodType<MerchantBackupMerchantAccountRecord> =
    z.object({
        merchant_account_uuid: z.string().uuid(),
        processor_name: z.string().min(1),
        processor_mid: z.string().min(1),
        organization_uuid: z.string().uuid(),
        applies_to_children: z.boolean(),
        status: z.enum(Status),
        merchant_account_configuration: JsonRecordSchema,
        omnisearch_text: z.string(),
    });

export const MerchantBackupLogicalTerminalRecordSchema: z.ZodType<MerchantBackupLogicalTerminalRecord> =
    z.object({
        logical_terminal_uuid: z.string().uuid(),
        merchant_account_uuid: z.string().uuid(),
        ipg_system_id: NumericIpgSystemIdSchema,
        processor_tid: z.string().min(1),
        organization_uuid: z.string().uuid().nullable(),
        applies_to_children: z.boolean().nullable(),
        status: z.enum(Status),
        logical_terminal_configuration: JsonRecordSchema,
        credential_uuid: z.string().uuid().nullable(),
        credential_credential_type: z.enum(CredentialType).nullable(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupTerminalDeviceRecordSchema: z.ZodType<MerchantBackupTerminalDeviceRecord> =
    z.object({
        terminal_device_uuid: z.string().uuid(),
        logical_terminal_uuid: z.string().uuid(),
        processor_device_id: z.string().min(1),
        organization_uuid: z.string().uuid().nullable(),
        applies_to_children: z.boolean().nullable(),
        name: z.string().nullable(),
        is_virtual: z.boolean(),
        status: z.enum(Status),
        terminal_device_configuration: JsonRecordSchema,
        credential_uuid: z.string().uuid().nullable(),
        credential_credential_type: z.enum(CredentialType).nullable(),
        omnisearch_text: z.string(),
    });

export const MerchantBackupV1Schema: z.ZodType<MerchantBackupV1> = z.object({
    schema_version: z.literal(1),
    exported_at_iso: z.string().min(1),
    root_organization_uuid: z.string().uuid(),
    organizations: z.array(MerchantBackupOrganizationRecordSchema),
    users: z.array(MerchantBackupUserRecordSchema),
    permissions_bundles: z.array(MerchantBackupPermissionsBundleRecordSchema),
    role_assignments: z.array(MerchantBackupRoleAssignmentRecordSchema),
    permissions_policy_grants: z.array(MerchantBackupPermissionsPolicyGrantRecordSchema),
    credentials: z.array(MerchantBackupCredentialRecordSchema),
    merchant_accounts: z.array(MerchantBackupMerchantAccountRecordSchema),
    logical_terminals: z.array(MerchantBackupLogicalTerminalRecordSchema),
    terminal_devices: z.array(MerchantBackupTerminalDeviceRecordSchema),
});

export const ExportMerchantBackupResponseSchema = z.object({
    backup: MerchantBackupV1Schema,
});

export const RestoreMerchantBackupDryRunRequestSchema = z.object({
    backup: MerchantBackupV1Schema,
});

export const RestoreMerchantBackupDryRunResponseSchema = z.object({
    valid: z.boolean(),
    warnings: z.array(z.string()),
    summary: z.object({
        organizations: z.number().int().nonnegative(),
        users: z.number().int().nonnegative(),
        permissions_bundles: z.number().int().nonnegative(),
        role_assignments: z.number().int().nonnegative(),
        permissions_policy_grants: z.number().int().nonnegative(),
        credentials: z.number().int().nonnegative(),
        merchant_accounts: z.number().int().nonnegative(),
        logical_terminals: z.number().int().nonnegative(),
        terminal_devices: z.number().int().nonnegative(),
    }),
});

export const RestoreMerchantBackupApplyRequestSchema = z.object({
    backup: MerchantBackupV1Schema,
});

export const RestoreMerchantBackupApplyResponseSchema = z.object({
    target_organization_uuid: z.string().uuid(),
    target_organization_name: z.string().min(1),
    summary: z.object({
        organizations: z.number().int().nonnegative(),
        users: z.number().int().nonnegative(),
        permissions_bundles: z.number().int().nonnegative(),
        role_assignments: z.number().int().nonnegative(),
        permissions_policy_grants: z.number().int().nonnegative(),
        credentials: z.number().int().nonnegative(),
        merchant_accounts: z.number().int().nonnegative(),
        logical_terminals: z.number().int().nonnegative(),
        terminal_devices: z.number().int().nonnegative(),
    }),
});

export const MerchantChecklistSeveritySchema = z.enum(['error', 'warning']);
export const MerchantChecklistStatusSchema = z.enum(['pass', 'error', 'warning']);
export const MerchantChecklistCheckCodeSchema = z.enum([
    'users_physical_terminal_access',
    'users_virtual_terminal_access',
    'merchant_business_details_complete',
    'logical_terminals_configured',
    'terminal_devices_configured',
    'terminal_device_credential_link_integrity',
    'credentials_gateway_connectivity',
    'credentials_inconsistent_by_account_id',
    'credentials_duplicated_by_account_id',
]);

export const MerchantChecklistCredentialSummarySchema: z.ZodType<
    MerchantChecklistCredentialIssue['credential']
> = z.object({
    credential_uuid: z.string().uuid(),
    account_id: z.string().min(1),
    system_id: z.number().int(),
    credential_type: z.enum(CredentialType),
    is_active: z.boolean(),
    applies_to_children: z.boolean(),
    public_fields: JsonRecordSchema,
});

export const MerchantChecklistCredentialIssueSchema: z.ZodType<MerchantChecklistCredentialIssue> =
    z.object({
        issue_type: z.literal('credential'),
        message: z.string().min(1),
        credential: MerchantChecklistCredentialSummarySchema,
    });

export const MerchantChecklistUserIssueSchema: z.ZodType<MerchantChecklistUserIssue> = z.object({
    issue_type: z.literal('user'),
    message: z.string().min(1),
    user: z.object({
        user_uuid: z.string().uuid(),
        primary_email: z.string().min(1),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        display_name: z.string().min(1),
    }),
});

export const MerchantChecklistPermissionIssueSchema: z.ZodType<MerchantChecklistPermissionIssue> =
    z.object({
        issue_type: z.literal('permission'),
        message: z.string().min(1),
        role_assignment_uuid: z.string().uuid(),
        permissions_bundle_uuid: z.string().uuid().nullable(),
        permissions_bundle_name: z.string().min(1).nullable(),
        permission_index: z.number().int().nonnegative().nullable(),
        resource_index: z.number().int().nonnegative().nullable(),
        finding_code: PermissionGrantAuditFindingCodeSchema,
        user: z.object({
            user_uuid: z.string().uuid(),
            primary_email: z.string().min(1),
            first_name: z.string().nullable(),
            last_name: z.string().nullable(),
            display_name: z.string().min(1),
        }),
    });

export const MerchantChecklistTerminalIssueSchema: z.ZodType<MerchantChecklistTerminalIssue> =
    z.object({
        issue_type: z.literal('terminal'),
        message: z.string().min(1),
        terminal: z.object({
            logical_terminal_uuid: z.string().uuid(),
            terminal_device_uuid: z.string().uuid().nullable(),
            processor_tid: z.string().min(1),
            processor_device_id: z.string().min(1).nullable(),
            is_virtual: z.boolean().nullable(),
            credential_uuid: z.string().uuid().nullable(),
            terminal_device_credential_type: z.enum(CredentialType).nullable(),
            linked_credential_type: z.enum(CredentialType).nullable(),
        }),
    });

export const MerchantChecklistIssueSchema = z.union([
    MerchantChecklistCredentialIssueSchema,
    MerchantChecklistUserIssueSchema,
    MerchantChecklistPermissionIssueSchema,
    MerchantChecklistTerminalIssueSchema,
]);

export const MerchantChecklistCheckResultSchema = z.object({
    code: z.string().min(1),
    title: z.string().min(1),
    severity: MerchantChecklistSeveritySchema,
    status: MerchantChecklistStatusSchema,
    details: z.array(z.string()),
    issues: z.array(MerchantChecklistIssueSchema),
});

export const MerchantChecklistSummarySchema = z.object({
    status: MerchantChecklistStatusSchema,
    error_count: z.number().int().nonnegative(),
    warning_count: z.number().int().nonnegative(),
});

export const GetMerchantChecklistResponseSchema: z.ZodType<GetMerchantChecklistResponse> = z.object({
    organization_uuid: z.string().uuid(),
    generated_at_iso: z.string().min(1),
    summary: MerchantChecklistSummarySchema,
    checks: z.array(MerchantChecklistCheckResultSchema),
});

export const GetMerchantChecklistCheckResponseSchema: z.ZodType<GetMerchantChecklistCheckResponse> =
    z.object({
        organization_uuid: z.string().uuid(),
        generated_at_iso: z.string().min(1),
        check: MerchantChecklistCheckResultSchema,
    });
